import { Modal, Table } from "antd";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { QuantityUnitDisplay } from "../../components/QuantityUnitDisplay.tsx";
import type { RecipeDetailFragment } from "../../gql/generated/graphqlSchema.tsx";
import { noWrap } from "../../styles/shared.styles.ts";
import { useCostFormatter } from "./recipeCost.ts";

type MeasurementUnit = { readonly id: string; readonly code: string };

type BreakdownRow = {
  readonly key: string;
  readonly label: string;
  readonly size: ReactNode;
  readonly net: number | null;
  readonly gross: number | null;
};

export const RecipeCostBreakdownModal = ({
  open,
  onClose,
  recipe,
  units,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly recipe: RecipeDetailFragment;
  readonly units: readonly MeasurementUnit[];
}) => {
  const { t } = useTranslation();
  const formatCost = useCostFormatter();
  const cost = recipe.referenceCost;

  const rows: BreakdownRow[] = [
    {
      key: "total",
      label: t("recipes.costBreakdown.total"),
      size:
        recipe.weightAfterCookingQuantity == null ? null : (
          <QuantityUnitDisplay
            quantity={recipe.weightAfterCookingQuantity}
            unitId={recipe.weightAfterCookingUnitId}
            units={units}
          />
        ),
      net: cost.totalNet ?? null,
      gross: cost.totalGross ?? null,
    },
    {
      key: "per100g",
      label: t("recipes.costBreakdown.per100g"),
      size: null,
      net: cost.per100gNet ?? null,
      gross: cost.per100gGross ?? null,
    },
    {
      key: "perKg",
      label: t("recipes.costBreakdown.perKg"),
      size: null,
      net: cost.perKgNet ?? null,
      gross: cost.perKgGross ?? null,
    },
    {
      key: "perServing",
      label: t("recipes.costBreakdown.perServing"),
      size:
        recipe.servingSizeQuantity == null ? null : (
          <QuantityUnitDisplay quantity={recipe.servingSizeQuantity} unitId={recipe.servingSizeUnitId} units={units} />
        ),
      net: cost.perServingNet ?? null,
      gross: cost.perServingGross ?? null,
    },
    {
      key: "perPackage",
      label: t("recipes.costBreakdown.perPackage"),
      size:
        recipe.netWeightQuantity == null ? null : (
          <QuantityUnitDisplay quantity={recipe.netWeightQuantity} unitId={recipe.netWeightUnitId} units={units} />
        ),
      net: cost.perPackageNet ?? null,
      gross: cost.perPackageGross ?? null,
    },
  ];

  return (
    <Modal destroyOnHidden footer={null} onCancel={onClose} open={open} title={t("recipes.costBreakdown.title")}>
      <Table<BreakdownRow>
        bordered
        columns={[
          {
            title: recipe.name,
            key: "label",
            render: (_, row) => (
              <strong className={noWrap}>
                {row.label}
                {row.size != null && <> ({row.size})</>}
              </strong>
            ),
            width: "100%",
          },
          {
            title: t("recipes.withoutVat"),
            key: "net",
            align: "right",
            render: (_, row) => <span className={noWrap}>{formatCost(row.net)}</span>,
          },
          {
            title: t("recipes.withVat"),
            key: "gross",
            align: "right",
            render: (_, row) => <span className={noWrap}>{formatCost(row.gross)}</span>,
          },
        ]}
        dataSource={rows}
        pagination={false}
        rowKey="key"
        size="small"
      />
    </Modal>
  );
};
